import React, { useState } from 'react';
import { Check, ArrowRight, Target, Code, Rocket, TrendingUp, Mail, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PartnershipForm from '../components/PartnershipForm';

const Applications = () => {
  const [isPartnershipFormOpen, setIsPartnershipFormOpen] = useState(false);
  const phases = [
    {
      id: 1,
      title: 'Phase 1: Foundation & Strategy',
      subtitle: 'Every great venture begins with clarity',
      description: 'In this foundational phase, we align your vision with market reality and engineering readiness.',
      icon: Target,
      color: '#ffed00',
      steps: [
        {
          number: '1',
          title: 'Ideation & Concept Validation',
          description: 'We refine your core idea, identify the target user segment, and define the unique value proposition that sets your venture apart.'
        },
        {
          number: '2',
          title: 'Business Modeling & Sustainability',
          description: 'We map out financial models, unit economics, and monetization or social-impact sustainability frameworks built for long-term resilience.'
        },
        {
          number: '3',
          title: 'Technical Architecture & Tech Stack Selection',
          description: 'We design a secure, scalable technical architecture and select the right AI models, databases, and hosting stacks for your specific use case.'
        }
      ]
    },
    {
      id: 2,
      title: 'Phase 2: Product & Iteration',
      subtitle: 'BUILD. TEST. LAUNCH.',
      description: '',
      icon: Code,
      color: '#ff8385',
      steps: [
        {
          number: '1',
          title: 'Core Product Development & Integration',
          description: 'We lead the engineering process to build a robust, user-centric product or platform — from backend APIs to polished front-end interfaces.'
        },
        {
          number: '2',
          title: 'Quality Assurance, Testing & Iteration',
          description: 'We run rigorous user-acceptance testing and squash bugs to optimize UI/UX flows based on early feedback from real users.'
        },
        {
          number: '3',
          title: 'Distribution & GTM Strategy',
          description: 'We craft a Go-To-Market strategy with community-driven content, local positioning, and platform-specific playbooks tailored to your audience.'
        }
      ]
    },
    {
      id: 3,
      title: 'Phase 3: Launch & Validation',
      subtitle: 'Deploy, measure, and iterate',
      description: 'With a product in hand, we move into the market — deploying, measuring, and relentlessly iterating toward Product-Market Fit.',
      icon: Rocket,
      color: '#faba00',
      steps: [
        {
          number: '1',
          title: 'MVP Launch & Deployment',
          description: 'We smoothly deploy the Minimum Viable Product to public app stores or live server environments with zero-downtime release strategies.'
        },
        {
          number: '2',
          title: 'Market Response & Analytics Tracking',
          description: 'We monitor real-time user behavior using analytics funnels to measure retention, drop-offs, and user sentiment across every touchpoint.'
        },
        {
          number: '3',
          title: 'Iterating Towards PMF',
          description: 'We constantly ship improvements based on live user data until retention figures stabilize and organic growth begins to compound.'
        }
      ]
    },
    {
      id: 4,
      title: 'Phase 4: Capital & Scale',
      subtitle: 'Secure funding and engineer for growth',
      description: 'With validated traction and a proven product, we shift focus to securing capital and engineering the infrastructure for institutional growth.',
      icon: TrendingUp,
      color: '#4eb5e6',
      steps: [
        {
          number: '1',
          title: 'Institutional Pitch Deck & Narrative Design',
          description: "We package the venture's data, vision, and growth metrics into a compelling pitch deck crafted for impact investors and institutional partners."
        },
        {
          number: '2',
          title: 'Fundraising: Investor Outreach & Crowdfunding',
          description: 'We leverage our network for investor access and design public crowdfunding campaigns to secure community-backed capital at scale.'
        },
        {
          number: '3',
          title: 'Growth Stage Engineering & Operational Scaling',
          description: 'We optimize infrastructure to handle large concurrent traffic, expand user acquisition loops, and transition the venture to institutional-grade operations.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="bg-[#ffed00] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-black">
              Partnership with Digital Inclusion Lab
            </h1>
            <p className="text-xl md:text-2xl text-black/80 leading-relaxed mb-8">
              A structured, end-to-end partnership framework designed to take your venture from raw idea to 
              institutional scale — built on proven methodology, community-driven strategy, and impact-first engineering.
            </p>
            <div 
              onClick={() => setIsPartnershipFormOpen(true)}
              className="inline-block bg-black text-white px-6 py-2 rounded-full font-bold cursor-pointer hover:bg-gray-900 transition-all duration-300 hover:scale-105"
            >
              STRATEGIC PARTNERSHIP
            </div>
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-20">
          {phases.map((phase, index) => {
            const IconComponent = phase.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div key={phase.id} className={`${isEven ? 'bg-gray-50' : 'bg-white'} rounded-3xl p-8 md:p-12`}>
                {/* Phase Header */}
                <div className="flex items-start gap-6 mb-12">
                  <div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: phase.color }}
                  >
                    <IconComponent className="w-10 h-10 text-black" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-4xl font-bold text-black mb-2">{phase.title}</h2>
                    <p className="text-2xl font-semibold text-black/70 mb-3">{phase.subtitle}</p>
                    {phase.description && (
                      <p className="text-lg text-gray-700 leading-relaxed">{phase.description}</p>
                    )}
                  </div>
                </div>

                {/* Phase Steps */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {phase.steps.map((step) => (
                    <div 
                      key={step.number}
                      className="bg-white rounded-2xl p-6 border-4 border-black hover:scale-105 transition-transform duration-300"
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-black font-bold text-2xl mb-4"
                        style={{ backgroundColor: phase.color }}
                      >
                        {step.number}
                      </div>
                      <h3 className="text-xl font-bold text-black mb-3">{step.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{step.description}</p>
                    </div>
                  ))}
                </div>

                {/* Special note for Phase 3 */}
                {phase.id === 3 && (
                  <div className="mt-8 bg-black text-white p-6 rounded-2xl">
                    <p className="text-lg font-semibold text-center">
                      Product-Market Fit is not a destination — it's a signal. We track it obsessively until the data confirms you've arrived.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#ff8385]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Together?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's turn your vision into institutional-scale impact.
          </p>

          <div className="max-w-md mx-auto mb-12">
            <a 
              href="mailto:bilal@digitalinclusionlab.com"
              className="bg-white text-black font-bold py-4 px-8 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              bilal@digitalinclusionlab.com
            </a>
          </div>

          <Button
            size="lg"
            onClick={() => setIsPartnershipFormOpen(true)}
            className="bg-black hover:bg-gray-900 text-white font-bold px-10 py-6 text-lg rounded-full border-4 border-white"
          >
            <Lightbulb className="mr-2 h-5 w-5" />
            Submit Your Idea Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Partnership Form Modal */}
      <PartnershipForm 
        open={isPartnershipFormOpen} 
        onOpenChange={setIsPartnershipFormOpen}
      />
    </div>
  );
};

export default Applications;
